import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { Sparkles, X, Send, RotateCcw, Copy, Check, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
export const AI_TOGGLE_EVENT = 'ai-assistant-toggle';
export const AI_BLOCKED_ROLES = ['lead_feeder', 'duch', 'guest'];

const projectIdFromPath = (path) => {
  const m = path.match(/^\/projects\/([^/]+)$/);
  return m && m[1] !== 'new' ? m[1] : null;
};

const suggestionsFor = (path, entity) => {
  if (entity?.accessible) {
    const what = entity.kind === 'lead' ? 'tego leada' : 'to zlecenie';
    return [`Podsumuj ${what}`, 'Zaproponuj następny krok', 'Napisz SMS do klienta', 'Napisz e-mail do klienta'];
  }
  if (path.startsWith('/leads') || path.startsWith('/skrzynka')) {
    return ['Ile leadów straciliśmy w tym miesiącu i dlaczego?', 'Które leady wymagają kontaktu?', 'Podsumuj moje leady'];
  }
  if (path.startsWith('/reports') || path.startsWith('/kpi')) {
    return ['Podsumuj wyniki tego miesiąca', 'Najczęstsze powody odrzuceń', 'Porównaj z poprzednim miesiącem'];
  }
  if (path.startsWith('/projects') || path.startsWith('/kanban') || path.startsWith('/montaze')) {
    return ['Które zlecenia utknęły bez kontaktu?', 'Podsumuj zlecenia w realizacji', 'Ile zleceń zakończyliśmy w tym miesiącu?'];
  }
  return ['Podsumuj sytuację w tym miesiącu', 'Ile leadów straciliśmy w tym miesiącu i dlaczego?', 'Które rekordy wymagają uwagi?'];
};

const CopyButton = ({ text, testId, label = 'Kopiuj' }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { toast.error('Nie udało się skopiować'); }
  };
  return (
    <button type="button" onClick={copy} data-testid={testId}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-card-hover)] transition-colors">
      {done ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}{done ? 'Skopiowano' : label}
    </button>
  );
};

const inline = (line) => line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
  part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part);

const TextBlock = ({ text }) => (
  <div className="space-y-1">
    {text.split('\n').map((raw, i) => {
      const line = raw.trimEnd();
      if (!line.trim()) return <div key={i} className="h-1" />;
      const bullet = line.match(/^\s*[*-]\s+(.*)$/);
      if (bullet) return <div key={i} className="flex gap-2 pl-1"><span className="text-[#FF6B35]">•</span><span>{inline(bullet[1])}</span></div>;
      return <p key={i}>{inline(line.replace(/^#+\s*/, ''))}</p>;
    })}
  </div>
);

const AssistantMessage = ({ content, index }) => {
  const parts = content.split(/```(?:szkic|text)?\n?([\s\S]*?)```/g);
  return (
    <div className="space-y-2">
      {parts.map((part, i) => (i % 2 === 1 ? (
        <div key={i} className="rounded-lg border border-[#FF6B35]/40 bg-[#FF6B35]/5 p-3" data-testid={`ai-draft-${index}-${i}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wider text-[#FF6B35] font-semibold flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Szkic wiadomości</span>
            <CopyButton text={part.trim()} testId={`ai-draft-copy-${index}-${i}`} />
          </div>
          <p className="whitespace-pre-wrap text-sm text-[var(--crm-text-primary)]">{part.trim()}</p>
        </div>
      ) : part.trim() && <TextBlock key={i} text={part.trim()} />))}
      <div className="flex justify-end"><CopyButton text={content.replace(/```(?:szkic|text)?\n?/g, '')} testId={`ai-message-copy-${index}`} label="Kopiuj całość" /></div>
    </div>
  );
};

const MessageList = ({ messages, sending, endRef }) => (
  <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="ai-messages">
    {messages.length === 0 && !sending && (
      <div className="text-center text-sm text-[var(--crm-text-secondary)] py-10" data-testid="ai-empty-state">
        <Sparkles className="w-8 h-8 mx-auto mb-3 text-[#FF6B35]" />
        Zapytaj o leady, zlecenia lub wyniki.<br />Asystent tylko czyta dane i pisze teksty – niczego nie zmienia.
      </div>
    )}
    {messages.map((m, i) => (
      <div key={i} className={m.role === 'user' ? 'flex justify-end' : ''} data-testid={`ai-message-${m.role}-${i}`}>
        <div className={m.role === 'user'
          ? 'max-w-[85%] rounded-2xl rounded-br-sm bg-[#FF6B35] text-white px-3.5 py-2 text-sm whitespace-pre-wrap'
          : 'text-sm text-[var(--crm-text-primary)] leading-relaxed'}>
          {m.role === 'user' ? m.content : <AssistantMessage content={m.content} index={i} />}
        </div>
      </div>
    ))}
    {sending && (
      <div className="flex items-center gap-2 text-sm text-[var(--crm-text-muted)]" data-testid="ai-typing">
        <Loader2 className="w-4 h-4 animate-spin text-[#FF6B35]" />Asystent analizuje dane...
      </div>
    )}
    <div ref={endRef} />
  </div>
);

const Composer = ({ onSend, sending, suggestions, remaining }) => {
  const [text, setText] = useState('');
  const submit = (value) => {
    const v = (value ?? text).trim();
    if (!v || sending) return;
    onSend(v);
    setText('');
  };
  return (
    <div className="border-t border-[var(--crm-border)] p-3 space-y-2" style={{ backgroundColor: 'var(--crm-bg-sidebar)' }}>
      <div className="flex gap-2 items-end">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Napisz pytanie..." data-testid="ai-input"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          className="resize-none bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] text-sm min-h-[44px]" />
        <Button onClick={() => submit()} disabled={sending || !text.trim()} data-testid="ai-send-btn"
          className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white h-11 w-11 p-0 shrink-0" aria-label="Wyślij">
          <Send className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="ai-suggestions">
        {suggestions.map((s, i) => (
          <button key={s} type="button" disabled={sending} onClick={() => submit(s)} data-testid={`ai-suggestion-${i}`}
            className="text-xs px-2.5 py-1 rounded-full border border-[var(--crm-border)] text-[var(--crm-text-secondary)] hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-50">
            {s}
          </button>
        ))}
      </div>
      {remaining !== null && <p className="text-[10px] text-[var(--crm-text-muted)]" data-testid="ai-remaining">Pozostało dziś: {remaining} wiadomości</p>}
    </div>
  );
};

export const AIAssistant = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [sending, setSending] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [entity, setEntity] = useState(null);
  const endRef = useRef(null);
  const allowed = user && !AI_BLOCKED_ROLES.includes(user.role) && !location.pathname.startsWith('/czas/');
  const storageKey = user ? `ai_conversation_${user.id}` : null;
  const projectId = projectIdFromPath(location.pathname);

  useEffect(() => {
    const toggle = () => setOpen((o) => !o);
    window.addEventListener(AI_TOGGLE_EVENT, toggle);
    return () => window.removeEventListener(AI_TOGGLE_EVENT, toggle);
  }, []);

  useEffect(() => { if (!allowed) setOpen(false); }, [allowed]);

  useEffect(() => {
    if (!open || !allowed) return;
    axios.get(`${API_URL}/ai/status`).then((r) => setRemaining(r.data.remaining)).catch(() => {});
    const saved = localStorage.getItem(storageKey);
    if (saved && !conversationId) {
      axios.get(`${API_URL}/ai/conversations/${saved}`)
        .then((r) => { setConversationId(saved); setMessages(r.data.messages || []); })
        .catch(() => localStorage.removeItem(storageKey));
    }
  }, [open, allowed, storageKey, conversationId]);

  useEffect(() => {
    if (!open || !allowed || !projectId) { setEntity(null); return; }
    axios.get(`${API_URL}/ai/context-info`, { params: { entity_id: projectId } })
      .then((r) => setEntity(r.data)).catch(() => setEntity(null));
  }, [open, allowed, projectId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const send = useCallback(async (text) => {
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setSending(true);
    try {
      const context = { path: location.pathname, ...(projectId ? { entity_type: 'project', entity_id: projectId } : {}) };
      const res = await axios.post(`${API_URL}/ai/chat`, { message: text, conversation_id: conversationId, context });
      setConversationId(res.data.conversation_id);
      localStorage.setItem(storageKey, res.data.conversation_id);
      setRemaining(res.data.remaining);
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Błąd asystenta AI');
      setMessages((m) => m.slice(0, -1));
    } finally { setSending(false); }
  }, [conversationId, location.pathname, projectId, storageKey]);

  const newConversation = () => {
    setMessages([]);
    setConversationId(null);
    localStorage.removeItem(storageKey);
  };

  if (!allowed) return null;

  return (
    <aside data-testid="ai-panel" aria-hidden={!open} inert={!open}
      className={`fixed top-0 right-0 z-[70] h-[100dvh] w-full md:w-[380px] flex flex-col border-l border-[var(--crm-border)] shadow-2xl transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}
      style={{ backgroundColor: 'var(--crm-bg-dialog)' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--crm-border)]" style={{ backgroundColor: 'var(--crm-bg-sidebar)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-[#FF6B35]/15 flex items-center justify-center"><Sparkles className="w-4 h-4 text-[#FF6B35]" /></div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[var(--crm-text-primary)]">Asystent AI</p>
            <p className="text-[11px] text-[var(--crm-text-muted)] truncate" data-testid="ai-context-label">
              {entity?.accessible ? `Kontekst: ${entity.kind === 'lead' ? 'lead' : 'zlecenie'} „${entity.name}”` : 'Kontekst: dane ogólne'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={newConversation} data-testid="ai-new-conversation-btn"
            className="text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] h-8 px-2 text-xs">
            <RotateCcw className="w-3.5 h-3.5 mr-1" />Nowa rozmowa
          </Button>
          <button onClick={() => setOpen(false)} data-testid="ai-close-btn" aria-label="Zamknij"
            className="p-1.5 rounded-md text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-card-hover)]">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      <MessageList messages={messages} sending={sending} endRef={endRef} />
      <Composer onSend={send} sending={sending} suggestions={suggestionsFor(location.pathname, entity)} remaining={remaining} />
    </aside>
  );
};

export const AIAssistantButton = () => {
  const { user } = useAuth();
  if (!user || AI_BLOCKED_ROLES.includes(user.role)) return null;
  return (
    <button type="button" onClick={() => window.dispatchEvent(new CustomEvent(AI_TOGGLE_EVENT))}
      data-testid="ai-assistant-toggle" title="Asystent AI" aria-label="Asystent AI"
      className="p-2 rounded-md text-[#FF6B35] hover:bg-[var(--crm-bg-card-hover)] transition-colors">
      <Sparkles className="w-5 h-5" />
    </button>
  );
};

export default AIAssistant;
